
import { Component } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { SharedModule } from 'src/app/shared/shared.module';
// import { ICellRendererParams, IAfterGuiAttachedParams } from 'ag-grid';

@Component({
  standalone: true,
  imports: [SharedModule],
  selector: 'app-name-renderer',
  templateUrl: './name-renderer.html'
})

export class NameRendererComponent implements ICellRendererAngularComp {

  params: any;
  label!: string;
  subtitle: string | null = null;
  isMobile: boolean = false;

  agInit(params: any): void {
    this.params = params;
    this.label = this.params.label || null;
    this.subtitle = this.params.subtitle || null;
    this.isMobile = !!this.params.isMobile;
  }

  // Check if this is a group node (folder)
  isGroupNode(): boolean {
    return this.params?.node?.group === true;
  }

  // Get the display value
  getDisplayValue(): string {
    if (this.isGroupNode()) {
      // For group nodes, show the folder name
      return this.params?.node?.key || 'General (No Folder)';
    }
    // For leaf nodes, show the media title
    return this.params?.data?.title || this.params?.value || '';
  }

  getSubtitle(): string {
    return this.subtitle || this.params?.data?.subtitle || '';
  }

  // Get appropriate CSS class for image container (now unified size)
  getImageContainerClass(): string {
    return 'table-image-container mr-2 cursor-pointer table-list-default-scrn-wrapper';
  }

  // Get appropriate CSS class for successful image containers  
  getSuccessImageContainerClass(): string {
    return 'table-image-container mr-2 cursor-pointer';
  }

  // Get appropriate CSS class for screen preview containers (TV shape)
  getScreenPreviewContainerClass(): string {
    return 'table-image-scrn-container mr-2 cursor-pointer';
  }

  refresh(params?: any): boolean {
    this.params = params;
    return true;
  }

  getImageClickAction(): string {
    return this.params?.imageClickAction || 'NAME';
  }

  // Screen-preview specific handlers. Unlike onClick('ERROR') these do NOT permanently
  // latch the failure: the <img> stays mounted, so when the 30s refresh swaps the src and
  // the blob finally exists, (load) fires and we clear the placeholder automatically.
  onScreenPreviewError(): void {
    if (this.params?.data) {
      this.params.data.urlError = true;
    }
  }

  onScreenPreviewLoad(): void {
    if (this.params?.data && this.params.data.urlError) {
      this.params.data.urlError = false;
    }
  }

  /** Web Player screens can never produce a live-preview screenshot (the browser can't be
   *  told to capture itself for security reasons) — this is expected, not broken, so it
   *  gets its own explanatory message instead of the generic "No preview available". */
  isWebPlayerPreview(): boolean {
    return !!this.params?.data?.playerType?.toUpperCase().includes('WEB');
  }

  previewUnavailableMessage(): string {
    return this.isWebPlayerPreview()
      ? 'Live previews are not available for web player given browser security limitations.'
      : 'No preview available';
  }

onClick(action: string, event?: Event): void {
  // Prevent default behavior and event bubbling if DOM event is provided
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }

  // Check if parent component has provided onClick handler
  if (this.params.onClick instanceof Function) {
    // Prepare parameters to pass to parent component
    const clickParams = {
      event: action,                    // The action type (ERROR, PREVIEW, NAME)
      rowData: this.params.node.data,   // The full row data
      rowIndex: this.params.rowIndex,   // Row index for reference
      colId: this.params.colDef?.colId, // Column ID for identification
      value: this.params.value,         // Current cell value
      originalEvent: event,             // Original DOM event (if any)
      // Additional context data
      ...(action === 'ERROR' && { 
        errorType: 'image_load_failed',
        imageUrl: this.params.data?.thumbnailUrl || this.params.data?.previewUrl || this.params.data?.iconUrl 
      }),
      ...(action === 'PREVIEW' && { 
        previewUrl: this.params.data?.thumbnailUrl || this.params.data?.previewUrl || this.params.data?.iconUrl,
        itemCode: this.params.data?.code 
      }),
      ...(action === 'NAME' && { 
        itemName: this.params.value,
        itemId: this.params.data?.id 
      })
    };

    // Call parent component's onClick handler
    this.params.onClick(clickParams);
  }

  // Handle specific actions locally if needed
  this.handleLocalActions(action);
}

private handleLocalActions(action: string): void {
  switch (action) {
    case 'ERROR':
      // Mark image as failed to prevent retry
      if (this.params.data) {
        this.params.data.urlError = true;
      }
      console.warn('Image failed to load for item:', this.params.data?.code || this.params.value);
      break;
      
    case 'PREVIEW':
      // Could add analytics tracking or other local handling
      break;
      
    case 'NAME':
      // Could add local state changes if needed
      break;
      
    default:
      console.warn('Unknown action:', action);
      break;
  }
}
}
